//@flow
import * as React from 'react';
import classnames from 'classnames';
import { themr } from 'react-css-themr';
import {
    locationMarkerFactory,
    ACTIVATE_ON,
    ALIGNMENT
} from 'common/components/core/markers/LocationMarker';
import theme from './RouteRestrictionMarker.scss';
import { Icon } from 'common/components/core/icon';
import { Button } from 'common/components/core/button';
import {
    KeyEventsDiv,
    DivWithPopover
} from 'common/components/core/wrappers/Div';
import { PopoverTriggerEnum } from 'common/components/core/popover/popOverFactory';
import TimeUtils from 'common/utils/TimeUtils';
import { MarkerType } from 'common/enums/MapEnum';
import { translate, Trans } from 'react-i18next';
import { dashboardColors } from 'common/enums/ColorEnum';
import Chip from 'common/components/core/common/Chip';
import type { RouteRestriction } from 'common/models/internal/RouteRestriction';
import type { TFunction } from 'react-i18next';
import * as Immutable from 'immutable';

const getVehicleList = (vehicleModelIds, vehicleTypes) => {
    let vehicleList = Immutable.List([]);
    if (vehicleModelIds.size > 0) {
        vehicleList = vehicleModelIds.map(
            vechileModelId => vechileModelId.modelId
        );
    }
    // Backward compatibility for older plans
    else if (vehicleTypes.size > 0) {
        vehicleList = vehicleTypes;
    }
    return vehicleList;
};

const getRestrictedVehicleListString = (vehicleModelIds, vehicleTypes, t) => {
    const vehicles = getVehicleList(vehicleModelIds, vehicleTypes);
    return vehicles.size > 1
        ? t('vehicleCount', { count: vehicles.size })
        : vehicles.get(0);
};

const getVehicleListPopoverContent = (vehicleModelIds, vehicleTypes) => {
    const vehicles = getVehicleList(vehicleModelIds, vehicleTypes);
    return vehicles.size > 1 ? vehicles.sort().join(', ') : null;
};

const getTimeSlotStrings = timeSlots => {
    return timeSlots.map(timeSlot => {
        const { start, end } = timeSlot;
        const startTime = TimeUtils.format(start.time, 'hh:mm a', '', {
            inputFormat: 'HH:mm:ss'
        });
        const endTime = TimeUtils.format(end.time, 'hh:mm a', '', {
            inputFormat: 'HH:mm:ss'
        });
        return `${startTime} – ${endTime}`;
    });
};

const PopupInner = ({
    routeRestriction,
    onPopupCloseClick,
    theme,
    isSelected,
    t
}: {
    routeRestriction: RouteRestriction,
    theme: Object,
    isSelected: boolean,
    onPopupCloseClick: () => void,
    t: TFunction
}) => {
    const {
        name,
        type,
        timeSlots,
        vehicleTypes,
        vehicleModelIds,
        polyline,
        enforcement,
        trafficFactor,
        restrictedDaysMap,
        restrictedDatesMap,
        daysForRestrictedDates,
        vehicleSkills,
        vehicleNumbers
    } = routeRestriction;

    const timeSlotStrings = getTimeSlotStrings(timeSlots);

    const vehicleListPopoverContent = getVehicleListPopoverContent(
        vehicleModelIds,
        vehicleTypes
    );
    const restrictedVehiclesString = getRestrictedVehicleListString(
        vehicleModelIds,
        vehicleTypes,
        t
    );
    const routeRestrictionBlockString = t('routeRestrictions.typeBlock', {
        type
    });
    const isHighTraffic = enforcement && enforcement === 'HIGH_TRAFFIC';
    const isNonStoppable = enforcement && enforcement === 'NON_STOPPABLE';
    const trafficFactorText = trafficFactor && `${trafficFactor}x`;

    /**
     * Generates the list of restricted time slots to be rendered
     *
     * @returns React.Node
     */
    const renderTimeSlots = () => {
        if (timeSlotStrings.size === 0) {
            return <>{t('routeRestrictions.noTimeSlots')}</>;
        }
        return timeSlotStrings.map((timeSlotString, index) => {
            return (
                <div key={index} className={theme.timeRange}>
                    {timeSlotString}
                </div>
            );
        });
    };

    const renderDayWiseRestrictions = () => {
        const dayWiseRestrictions = [];
        restrictedDaysMap.map((days, vehicleNumbers) => {
            const daysSplit = days.split(',');
            const translatedDaysString = daysSplit
                .map(day => {
                    return t(`days.${day.trim().toUpperCase()}`);
                })
                .join(', ');

            return dayWiseRestrictions.push(
                <div key={vehicleNumbers} className={theme.restrictedDays}>
                    {`days - ${vehicleNumbers}: ${translatedDaysString}`}
                </div>
            );
        });
        if (dayWiseRestrictions.length) {
            return (
                <div className={theme.dayWiseRestrictions}>
                    {dayWiseRestrictions}
                </div>
            );
        }
        return null;
    };

    const renderDateWiseRestrictions = () => {
        const dateWiseRestrictions = [];
        restrictedDatesMap.map((dates, vehicleNumbers) => {
            return dateWiseRestrictions.push(
                <div key={vehicleNumbers} className={theme.restrictedDays}>
                    {`dates - ${vehicleNumbers}: ${dates}`}
                </div>
            );
        });
        if (dateWiseRestrictions.length) {
            return (
                <div className={theme.dayWiseRestrictions}>
                    {!daysForRestrictedDates.isEmpty() ? (
                        <div className={theme.dayWiseRestrictions}>
                            <strong>
                                {t('routeRestrictions.applicableDays')}
                            </strong>{' '}
                            <i>{daysForRestrictedDates.join(', ')}</i>
                        </div>
                    ) : null}
                    {dateWiseRestrictions}
                </div>
            );
        }
        return null;
    };

    const renderVehicleSkills = () => {
        if (vehicleSkills.isEmpty()) {
            return null;
        }

        return (
            <div className={theme.dayWiseRestrictions}>
                <strong>{t('routeRestrictions.skills')}</strong>{' '}
                <i>{vehicleSkills.join(', ')}</i>
            </div>
        );
    };

    const renderVehicleNumbers = () => {
        if (vehicleNumbers.isEmpty()) {
            return null;
        }

        return (
            <div className={theme.dayWiseRestrictions}>
                <strong>{t('routeRestrictions.vehicleNumbers')}</strong>{' '}
                <i>{vehicleNumbers.join(', ')}</i>
            </div>
        );
    };

    return (
        <KeyEventsDiv
            onEscape={onPopupCloseClick}
            className={theme.popupContainer}
        >
            <Button
                theme={theme}
                icon="cancel-thin"
                className={classnames(theme.closeBtn, {
                    [theme.visible]: isSelected
                })}
                onClick={onPopupCloseClick}
            />
            <div className={theme.title}>
                <div className={theme.left}>
                    {enforcement ? (
                        isHighTraffic ? (
                            <Trans i18nKey="routeRestrictions.highTraffic">
                                <span
                                    className={classnames(
                                        theme.semibold,
                                        theme.highlight
                                    )}
                                >
                                    High Traffic
                                </span>
                                {{ type }}
                            </Trans>
                        ) : isNonStoppable ? (
                            <Trans i18nKey="routeRestrictions.nonStoppable">
                                <span
                                    className={classnames(
                                        theme.semibold,
                                        theme.highlight
                                    )}
                                >
                                    Non Stoppable
                                </span>
                                {{ type }}
                            </Trans>
                        ) : (
                            <Trans i18nKey="routeRestrictions.strict">
                                <span
                                    className={classnames(
                                        theme.semibold,
                                        theme.highlight
                                    )}
                                >
                                    Strict
                                </span>
                                {{ type }}
                            </Trans>
                        )
                    ) : (
                        routeRestrictionBlockString
                    )}
                </div>
                {isHighTraffic ? (
                    <Chip
                        theme={theme}
                        className={theme.trafficFactorChip}
                        text={trafficFactorText}
                        backgroundColor={dashboardColors.salmon.name}
                    />
                ) : null}
            </div>
            {renderTimeSlots()}
            {renderDayWiseRestrictions()}
            {renderDateWiseRestrictions()}
            {renderVehicleSkills()}
            {renderVehicleNumbers()}

            <DivWithPopover
                disableAnimation
                popOverContent={vehicleListPopoverContent}
                theme={theme}
                popOverClassName={theme.vehicleNamePopover}
                triggerPopoverOn={PopoverTriggerEnum.Hover}
                className={theme.restrictedVehicles}
            >
                {polyline ? (
                    <Trans i18nKey="routeRestrictions.polylineRestrictionMessage">
                        <span
                            className={classnames(
                                theme.semibold,
                                theme.highlight
                            )}
                        >
                            <span>A</span>{' '}
                            <Icon
                                icon="arrow-solid-right"
                                theme={theme}
                                className={theme.arrowRightIcon}
                            />{' '}
                            <span>B</span>
                        </span>{' '}
                        <span>Restricted</span> ({{ restrictedVehiclesString }})
                    </Trans>
                ) : (
                    <Trans i18nKey="routeRestrictions.restrictionMessage">
                        <span
                            className={classnames(
                                theme.semibold,
                                theme.highlight
                            )}
                        >
                            {{ restrictedVehiclesString }}
                        </span>{' '}
                        Restricted
                    </Trans>
                )}
            </DivWithPopover>
            <div className={theme.restrictionName}>{name}</div>
        </KeyEventsDiv>
    );
};
const MarkerWithPopup = locationMarkerFactory({
    PopupInner
});

type Props = {
    routeRestriction: RouteRestriction,
    theme: Object,
    routeRestrictionId: string,
    $hover: boolean,
    isPopupOpen: boolean,
    onPopupCloseClick?: () => void,
    onClick?: (routeRestrictionId: string) => void,
    $onMouseAllow: (allowMouse: boolean) => void,
    onHover: Function,
    onHoverOut: Function,
    t: TFunction
};

class RouteRestrictionMarker extends React.PureComponent<Props> {
    UNSAFE_componentWillReceiveProps(nextProps) {
        var { $hover, routeRestrictionId, onHover, onHoverOut } = nextProps;
        if (!this.props.$hover && $hover) {
            onHover && onHover(routeRestrictionId);
        } else if (this.props.$hover && !$hover) {
            onHoverOut && onHoverOut(routeRestrictionId);
        }
    }

    onClick = () => {
        const { onClick, routeRestrictionId } = this.props;
        onClick && onClick(routeRestrictionId);
    };

    onPopupCloseClick = () => {
        const {
            onPopupCloseClick,
            routeRestrictionId,
            $onMouseAllow
        } = this.props;
        $onMouseAllow(true);
        onPopupCloseClick && onPopupCloseClick(routeRestrictionId);
    };

    render() {
        const {
            theme,
            routeRestriction,
            isPopupOpen,
            onPopupCloseClick, //eslint-disable-line no-unused-vars
            routeRestrictionId, //eslint-disable-line no-unused-vars
            onClick, //eslint-disable-line no-unused-vars,
            isCanvasMarker, //eslint-disable-line no-unused-vars,
            canvasShape, //eslint-disable-line no-unused-vars,
            sideLength,
            ...others
        } = this.props;

        return (
            <MarkerWithPopup
                dangerouslyDisablePopupHover
                onPopupCloseClick={this.onPopupCloseClick}
                routeRestriction={routeRestriction}
                isSelected={isPopupOpen}
                showPopupOn={ACTIVATE_ON.SELFHOVERSELECT}
                onClick={this.onClick}
                theme={theme}
                className={theme.routeRestrictionMarker}
                sideLength={sideLength}
                markerIcon={<Icon icon="alert" theme={theme} />}
                withArrow={false}
                markerType={MarkerType.HOVERABLE}
                markerAlignment={ALIGNMENT.Center}
                {...others}
            />
        );
    }
}

const ThemedRouteRestrictionMarker = translate('translations')(
    themr('RouteRestrictionMarker', theme)(RouteRestrictionMarker)
);

export default ThemedRouteRestrictionMarker;
